import type { PlainMessage } from '@bufbuild/protobuf';

import {
  NoteDentition,
  NoteTeethGranularity,
  NoteTeethGroup,
  type NoteTeethBlock,
  type NoteToothCode,
  type NoteToothMark,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { NoteToothNotation } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import { printNoteNumber, printNoteSurface } from './noteNumbers';
import { formatNoteTooth } from './noteView';
import { isToothInNoteTeethGroup } from './teethChart';

export type NoteMark = PlainMessage<NoteToothMark>;

type MarksBlock = PlainMessage<NoteTeethBlock>;

// How a teeth field that stores marks is filled: codes picked per tooth,
// one code cycled per tooth, a code per surface, or a number per tooth.
export type NoteTeethMarksPresentation =
  | 'codes'
  | 'cycle'
  | 'surfaces'
  | 'numbers';

export const readNoteTeethMarksPresentation = (
  block: MarksBlock,
): NoteTeethMarksPresentation => {
  if (block.Unit) {
    return 'numbers';
  }
  if (block.Granularity === NoteTeethGranularity.NoteTeethGranularitySurface) {
    return 'surfaces';
  }
  return block.OneCodePerTooth ? 'cycle' : 'codes';
};

const isPrimaryTooth = (tooth: string) => Number(tooth.charAt(0)) > 4;

// The codes that apply to a tooth: a code limited to permanent or primary
// teeth applies to those only.
export const listNoteToothCodes = (block: MarksBlock, tooth: string) =>
  block.Codes.filter((code) => {
    switch (code.Dentition) {
      case NoteDentition.NoteDentitionPermanent:
        return !isPrimaryTooth(tooth);
      case NoteDentition.NoteDentitionPrimary:
        return isPrimaryTooth(tooth);
      default:
        return true;
    }
  });

// The chart's rows: primary rows too when the field's dentition is mixed or
// one of its codes applies to primary teeth only.
export const readNoteTeethMarksDentition = (block: MarksBlock) =>
  block.Dentition === NoteDentition.NoteDentitionPermanent &&
  block.Codes.some(
    (code) => code.Dentition === NoteDentition.NoteDentitionPrimary,
  )
    ? NoteDentition.NoteDentitionMixed
    : block.Dentition;

// What a tooth holds, a mark set on one of its groups included until scribe
// spells the group out per tooth.
export const listNoteToothMarks = (marks: NoteMark[], tooth: string) =>
  marks.filter((mark) =>
    mark.Tooth
      ? mark.Tooth === tooth
      : mark.Group !== NoteTeethGroup.NoteTeethGroupInvalidValue &&
        isToothInNoteTeethGroup(tooth, mark.Group),
  );

export const readNoteToothCodes = (marks: NoteMark[], tooth: string) =>
  listNoteToothMarks(marks, tooth)
    .filter((mark) => !mark.Surface)
    .flatMap((mark) => mark.Codes);

const toothMark = (tooth: string, mark: Partial<NoteMark>): NoteMark => ({
  Tooth: tooth,
  Codes: [],
  Group: NoteTeethGroup.NoteTeethGroupInvalidValue,
  ...mark,
});

// The marks with a tooth's own mark, without a surface, replaced.
const replaceToothMark = (
  marks: NoteMark[],
  tooth: string,
  mark: NoteMark | null,
) => [
  ...marks.filter((item) => item.Tooth !== tooth || !!item.Surface),
  ...(mark ? [mark] : []),
];

export const setNoteToothCodes = (
  marks: NoteMark[],
  tooth: string,
  codes: string[],
) =>
  replaceToothMark(
    marks,
    tooth,
    codes.length ? toothMark(tooth, { Codes: codes }) : null,
  );

// The next code of a tooth's codes, and none after the last.
export const cycleNoteToothCode = (
  block: MarksBlock,
  marks: NoteMark[],
  tooth: string,
) => {
  const codes = listNoteToothCodes(block, tooth).map((code) => code.Code);
  const [current] = readNoteToothCodes(marks, tooth);
  const next = codes[current ? codes.indexOf(current) + 1 : 0];
  return setNoteToothCodes(marks, tooth, next ? [next] : []);
};

// A surface takes one code; none takes the surface's mark off.
export const setNoteSurfaceCode = (
  marks: NoteMark[],
  tooth: string,
  surface: string,
  code: string | null,
) => [
  ...marks.filter((mark) => mark.Tooth !== tooth || mark.Surface !== surface),
  ...(code ? [toothMark(tooth, { Surface: surface, Codes: [code] })] : []),
];

export const readNoteSurfaceCode = (
  marks: NoteMark[],
  tooth: string,
  surface: string,
) =>
  listNoteToothMarks(marks, tooth).find((mark) => mark.Surface === surface)
    ?.Codes[0];

export const setNoteToothNumber = (
  marks: NoteMark[],
  tooth: string,
  number: string | null,
) =>
  replaceToothMark(
    marks,
    tooth,
    number ? toothMark(tooth, { Number: number }) : null,
  );

export const readNoteToothNumber = (marks: NoteMark[], tooth: string) =>
  listNoteToothMarks(marks, tooth).find((mark) => mark.Number)?.Number;

// A mark set on a group: scribe sets it on each tooth of the group the field
// offers.
export const addNoteGroupMark = (
  marks: NoteMark[],
  group: NoteTeethGroup,
  codes: string[],
) => [...marks, toothMark('', { Group: group, Codes: codes })];

// The five zones of a tooth as drawn: the top is the vestibular surface of
// an upper tooth and the oral one of a lower; quadrants 1, 4, 5 and 8 have
// the distal surface on the left; the centre is incisal on incisors.
export const listNoteToothZones = (tooth: string) => {
  const quadrant = Number(tooth.charAt(0));
  const upper = [1, 2, 5, 6].includes(quadrant);
  const distalLeft = [1, 4, 5, 8].includes(quadrant);
  const incisor = ['1', '2'].includes(tooth.charAt(1));
  return {
    top: upper ? 'vestibular' : 'oral',
    bottom: upper ? 'oral' : 'vestibular',
    left: distalLeft ? 'distal' : 'mesial',
    right: distalLeft ? 'mesial' : 'distal',
    centre: incisor ? 'incisal' : 'occlusal',
  };
};

export const findNoteToothCode = (block: MarksBlock, code: string) =>
  block.Codes.find((item) => item.Code === code);

// A code as the chart shows it on a tooth: its abbreviation, or its label.
export const showNoteToothCode = (code: PlainMessage<NoteToothCode>) =>
  code.Short || code.Label;

// The marks as scribe prints them (`marksText`): the teeth of one value
// together, the value after them, the values apart by "; ". Before scribe
// answers, a mark set on a group is not printed.
export const printNoteTeethMarks = (
  block: MarksBlock,
  marks: NoteMark[],
  { notation, language }: { notation?: NoteToothNotation; language?: string },
) => {
  const valueOf = (mark: NoteMark) =>
    mark.Number
      ? printNoteNumber(mark.Number, block.Unit, language)
      : mark.Codes.map(
          (code) => findNoteToothCode(block, code)?.Label ?? code,
        ).join(', ');
  const entries = marks
    .filter((mark) => mark.Tooth)
    .reduce<{ tooth: string; surfaces: string; value: string }[]>(
      (current, mark) => {
        const value = valueOf(mark);
        const tooth = formatNoteTooth(mark.Tooth, notation);
        const last = current.at(-1);
        if (
          last &&
          mark.Surface &&
          last.tooth === tooth &&
          last.value === value
        ) {
          return [
            ...current.slice(0, -1),
            {
              ...last,
              surfaces:
                last.surfaces + printNoteSurface(mark.Surface, language),
            },
          ];
        }
        return [
          ...current,
          {
            tooth,
            value,
            surfaces: mark.Surface
              ? printNoteSurface(mark.Surface, language)
              : '',
          },
        ];
      },
      [],
    );
  const values = entries
    .map((entry) => entry.value)
    .filter((value, index, all) => all.indexOf(value) === index);
  return values
    .map((value) => {
      const teeth = entries
        .filter((entry) => entry.value === value)
        .map((entry) =>
          entry.surfaces ? `${entry.tooth} ${entry.surfaces}` : entry.tooth,
        )
        .join(', ');
      return value ? `${teeth}: ${value}` : teeth;
    })
    .join('; ');
};
